<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { page } from '$stores/pageStore';
  const browser = typeof window !== 'undefined';
  import PopupForm from './studies/StudyForm.svelte';
  import StudyPropertiesEditor from './studies/StudyPropertiesEditor.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import PortalDetails from '$lib/PortalDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faSquarePlus,
    faTrashCan,
    faPencil,
    faSearch,
    faCircleXmark,
    faCog,
    faHistory,
    faEllipsis,
  } from '@fortawesome/pro-light-svg-icons';
  import { user, userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAuditLogAccess, hasAccessUiAction } from '$utils/permissionUtils.js';
  import { tr, le, tui, selectedLanguage } from '$stores/translationStore';
  import StudyDetails from '$lib/StudyDetails.svelte';
  
  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  // Convert language code from en_US format to en-US format
  function getLocaleFromLanguage(lang) {
    if (!lang) return undefined;
    return lang.replace('_', '-');
  }

  let studies = [];

  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let searchText = '';

  let showForm = false;
  let showPropertiesEditor = false;
  let showConfirmation = false;
  let showAuditLog = false;
  let editStudy = null;
  let studyToDelete = null;
  let auditLogStudy = null;
  let showMenu = false;
  let menuStudy = null;
  let menuPosition = { top: 0, left: 0, direction: 'down' };
  let errorMessage = '';
  let showError = false;

  // Add computed property for edit permissions using the centralized hasRole function
  $: canAddStudies = hasAccessUiAction('studies.add', $userRoles, $studyProperties, $userReadOnly);
  $: canEditStudies = hasAccessUiAction('studies.edit', $userRoles, $studyProperties, $userReadOnly);
  $: canConfigureStudies = hasAccessUiAction('studies.configure', $userRoles, $studyProperties, $userReadOnly);
  $: canDeleteStudies = hasAccessUiAction('studies.delete', $userRoles, $studyProperties, $userReadOnly);
  $: canViewAuditLogs = hasAuditLogAccess($userRoles, $studyProperties, $userReadOnly);

  onMount(async () => {
    try {
      // Debug log to see user structure
      // console.log('User object structure:', JSON.stringify($user, null, 2));
      
      selectedStudy.set(null);
      selectedSite.set(null);
      selectedParticipant.set(null);

      // Get search text from URL parameter if it exists
      const urlSearchText = $page.url.searchParams.get('search');
      if (urlSearchText) {
        searchText = urlSearchText;
      }

      // Get page number from URL parameter if it exists
      const urlPage = parseInt($page.url.searchParams.get('page')) || 1;
      currentPage = urlPage;

      await fetchStudies(currentPage);
    } catch (err) {
      console.error('Error initializing component:', err);
    }
  });

  async function fetchStudies(page = 1) {
    try {
      const res = await fetch(
        `${API_BASE_URL}/studies?page=${page}&pageSize=${limit}&searchText=${searchText}&sortOrder=asc`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch studies');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      if (demoMode) {
        studies = responseData.data.map((study) => ({
          ...study,
          studyName: `Demo Study ${study.studyId}`,
          protocolNumber: `Protocol ${study.studyId}`,
          sponsor: 'PDH',
        }));
      } else {
        studies = responseData.data;
      }
    } catch (err) {
      console.error('Error fetching studies:', err);
    }
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      // Update URL with page number
      const url = new URL(window.location);
      url.searchParams.set('page', newPage.toString());
      goto(url.toString(), { replaceState: true });

      fetchStudies(newPage);
    }
  }

  async function handleCreateStudy(event) {
    const newStudy = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/studies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newStudy),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to create study');
      }

      await fetchStudies();
      showForm = false;
    } catch (err) {
      console.error('Error creating study:', err);
      errorMessage = err.message;
      showError = true;
      // Hide error after 5 seconds
      setTimeout(() => {
        showError = false;
        errorMessage = '';
      }, 5000);
    }
  }

  async function handleEditStudy(event) {
    const updatedStudy = event.detail;
    //console.log('handleEditStudy called with updatedStudy:', updatedStudy);
    try {
      const res = await fetch(`${API_BASE_URL}/studies/${updatedStudy._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedStudy),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to update study');
      }

      await fetchStudies();
      showForm = false;
      editStudy = null;
    } catch (err) {
      console.error('Error updating study:', err);
      errorMessage = err.message;
      showError = true;
      // Hide error after 5 seconds
      setTimeout(() => {
        showError = false;
        errorMessage = '';
      }, 5000);
    }
  }

  async function confirmDelete() {
    if (studyToDelete) {
      try {
        const res = await fetch(`${API_BASE_URL}/studies/${studyToDelete.studyId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!res.ok) {
          throw new Error('Failed to delete study');
        }

        await fetchStudies();
        showConfirmation = false;
        studyToDelete = null;
      } catch (err) {
        console.error('Error deleting study:', err);
      }
    }
  }

  function handleDeleteClick(study) {
    studyToDelete = study;
    showConfirmation = true;
  }

  function handleStudyClick(study) {
    // console.log('Study clicked, setting selectedStudy:', study);
    
    // Set the selected study
    selectedStudy.set(study);
    
    // Clear other selections
    selectedSite.set(null);
    selectedParticipant.set(null);
    
    // Make sure the study is saved to localStorage before navigation
    // by forcing a write to localStorage
    if (browser) {
      localStorage.setItem('selectedStudy', JSON.stringify(study));
      // console.log('Manually saved study to localStorage:', study);
      
      // Small delay to ensure localStorage is updated before navigation
      setTimeout(() => {
        // console.log('Navigating to sites page with study:', study._id);
        // goto(`/sites?debug=true`);
        goto(`/sites`);
      }, 200);
    } else {
      goto(`/sites`);
    }
  }

  function handleEditClick(study) {
    editStudy = study;
    showForm = true;
  }

  function handleNewClick() {
    editStudy = null;
    showForm = true;
  }

  function handleSearch() {
    // Update URL with search text and reset page to 1
    const url = new URL(window.location);
    if (searchText) {
      url.searchParams.set('search', searchText);
    } else {
      url.searchParams.delete('search');
    }
    url.searchParams.set('page', '1'); // Reset to page 1 when searching
    goto(url.toString(), { replaceState: true });
    fetchStudies(1);
  }

  function handleClearSearch() {
    searchText = '';
    // Reset page to 1 when clearing search
    currentPage = 1;
    handleSearch();
  }

  function handleKeydown(event) {
    if (event.key === 'Enter') {
      handleSearch();
    }
  }

  function handlePropertiesClick(study) {
    selectedStudy.set(study);
    showPropertiesEditor = true;
  }

  function handleAuditLogClick(study) {
    auditLogStudy = study;
    showAuditLog = true;
  }

  function handleMenuClick(study, event) {
    event.stopPropagation();
    menuStudy = study;
    // Get button position
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const menuHeight = 150; // Approximate, adjust as needed
    const menuWidth = 192; // w-48 in px
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    let direction = 'down';
    let top = buttonRect.bottom + window.scrollY;
    let left = buttonRect.left + window.scrollX;
    if (buttonRect.bottom + menuHeight > viewportHeight) {
      direction = 'up';
      top = buttonRect.top + window.scrollY - menuHeight;
      if (top < 0) top = 0;
    }
    if (left + menuWidth > viewportWidth - 8) {
      left = viewportWidth - menuWidth - 8;
      if (left < 0) left = 0;
    }
    menuPosition = { top, left, direction };
    showMenu = true;
  }

  function handleMenuClose() {
    showMenu = false;
    menuStudy = null;
  }

  // Add click handler to close menu when clicking outside
  function handleClickOutside(event) {
    if (showMenu && !event.target.closest('.menu-container')) {
      handleMenuClose();
    }
  }

  onMount(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });
</script>

<PopupForm
  {showForm}
  {editStudy}
  on:submit={editStudy ? handleEditStudy : handleCreateStudy}
  on:close={() => (showForm = false)}
/>

{#if showError}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75"></div>
    <div class="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg max-w-md w-full mx-4 relative z-60">
      <div class="flex items-center mb-4">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
            <svg class="w-5 h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
            </svg>
          </div>
        </div>
        <div class="ml-3">
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">
            Error
          </h3>
        </div>
      </div>
      <div class="mt-2">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          {errorMessage}
        </p>
      </div>
      <div class="mt-4 flex justify-end">
        <button
          on:click={() => {
            showError = false;
            errorMessage = '';
          }}
          class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700"
        >
          Close
        </button>
      </div>
    </div>
  </div>
{/if}

<StudyPropertiesEditor
  showForm={showPropertiesEditor}
  on:close={() => (showPropertiesEditor = false)}
/>

<ConfirmationModal
  {showConfirmation}
  message={
    tui($tr, $le, 'studies.deleteConfirm', 'Are you sure you want to delete this study:') + ' ' + (studyToDelete?.protocolNumber || 'Unknown') + '?'
  }
  on:confirm={confirmDelete}
  on:close={() => (showConfirmation = false)}
/>

<AuditLogModal
  showModal={showAuditLog}
  documentId={auditLogStudy?._id}
  collection="Study"
  title={
    tui($tr, $le, 'studies.auditLogsTitle', 'Audit Logs - ') + ' ' + (auditLogStudy?.studyName || 'Study')
  }
  on:close={() => (showAuditLog = false)}
/>

<StudyHeader />

<FeatureInfo 
  title={tui($tr, $le, 'studies.title', 'Studies Management')}
  description={tui($tr, $le, 'studies.description', 'This page allows you to view and manage all studies in the system. You can search, create, edit, and delete studies as well as configure study properties.')}
  marginBottom="mb-0"
/>

<PortalDetails /> 

<StudyDetails 
  showOptionsMenu={false}
  on:edit={(e) => {
    console.log('Edit event received:', e.detail);
    editStudy = e.detail;
    showForm = true;
  }}
  on:configure={(e) => {
    console.log('Configure event received:', e.detail);
    selectedStudy.set(e.detail);
    showPropertiesEditor = true;
  }}
  on:auditLog={(e) => {
    console.log('Audit log event received:', e.detail);
    auditLogStudy = e.detail;
    showAuditLog = true;
  }}
/>

<Toolbar
  title={tui($tr, $le, 'studies.toolbarTitle', 'Studies')}
  itemCountTitle={tui($tr, $le, 'studies.itemCountTitle', 'Studies')}
  itemCount={resultCount}
>
  <div class="flex items-center gap-4">
    <div class="flex items-center gap-2">
      <div class="relative">
        <input
          type="text"
          bind:value={searchText}
          placeholder="Search"
          on:keydown={handleKeydown}
          class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
        />
        {#if searchText}
          <button
            on:click={handleClearSearch}
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title={tui($tr, $le, 'shared.clearSearch', 'Clear search')}
          >
            <FontAwesomeIcon icon={faCircleXmark} />
          </button>
        {/if}
      </div>
      <button on:click={handleSearch} title={tui($tr, $le, 'shared.searchBtn', 'Search')}>
        <FontAwesomeIcon icon={faSearch} />
      </button>
    </div>
    <button
      on:click={handleNewClick}
      class="pagination mx-1 p-0"
      disabled={!canAddStudies}
      class:opacity-50={!canAddStudies}
      title={!canAddStudies
        ? tui($tr, $le, 'shared.insufficientPermissions', 'Insufficient permissions')
        : tui($tr, $le, 'studies.addNew', 'Add new study')
      }
    >
      <FontAwesomeIcon icon={faSquarePlus} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  {#if studies.length > 0}
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">{tui($tr, $le, 'studies.name', 'Name')}</th>
          <th class="px-4 py-2 text-left bold">{tui($tr, $le, 'studies.protocol', 'Protocol')}</th>
          <th class="px-4 py-2 text-left bold">{tui($tr, $le, 'studies.sponsor', 'Sponsor')}</th>
          <th class="px-4 py-2 text-left bold">{tui($tr, $le, 'studies.startDate', 'Start Date')}</th>
          <th class="px-4 py-2 text-left bold">{tui($tr, $le, 'studies.endDate', 'End Date')}</th>
          <th class="px-4 py-2 text-left bold">{tui($tr, $le, 'studies.status', 'Status')}</th>
          <th class="px-4 py-2 text-left bold w-8">&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        {#each studies as study, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2 cursor-pointer" on:click={() => handleStudyClick(study)}>{study.studyName}</td>
            <td class="px-4 py-2 cursor-pointer" on:click={() => handleStudyClick(study)}>{study.protocolNumber}</td>
            <td class="px-4 py-2 cursor-pointer" on:click={() => handleStudyClick(study)}>{study.sponsor ? study.sponsor : ''}</td>
            <td class="px-4 py-2 cursor-pointer" on:click={() => handleStudyClick(study)}>{study.startDate ? new Date(study.startDate).toLocaleDateString(getLocaleFromLanguage($selectedLanguage)) : ''}</td>
            <td class="px-4 py-2 cursor-pointer" on:click={() => handleStudyClick(study)}>{study.stopDate ? new Date(study.stopDate).toLocaleDateString(getLocaleFromLanguage($selectedLanguage)) : ''}</td>
            <td class="px-4 py-2" on:click={() => handleStudyClick(study)}>
              <span
                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                class:text-green-600={study.statusLabel === 'Active'}
                class:ring-green-600={study.statusLabel === 'Active'}
                class:text-gray-600={study.statusLabel === 'Closed'}
                class:ring-gray-600={study.statusLabel === 'Closed'}
                class:text-sky-600={study.statusLabel === 'Pre-Live'}
                class:ring-sky-600={study.statusLabel === 'Pre-Live'}
              >
                {study.statusLabel}
              </span>
            </td>
            <td class="py-2">
              <div class="menu-container relative">
                <button
                  on:click={(e) => handleMenuClick(study, e)}
                  class="pagination mx-1 p-0"
                  title={tui($tr, $le, 'shared.moreOptions', 'More options')}
                >
                  <FontAwesomeIcon icon={faEllipsis} class="text-sky-800 dark:text-white/70" />
                </button>
                {#if showMenu && menuStudy?._id === study._id}
                  <div
                    class="fixed w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 menu-dropdown"
                    style="top: {menuPosition.top}px; left: {menuPosition.left}px; max-height: 200px; overflow-y: auto;"
                  >
                    <button
                      on:click={() => {
                        handleEditClick(study);
                        handleMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      disabled={!canEditStudies}
                      class:opacity-50={!canEditStudies}
                      title={!canEditStudies ? tui($tr, $le, 'shared.insufficientPermissions', 'Insufficient permissions') : ''}
                    >
                      <span>{tui($tr, $le, 'studies.edit', 'Edit study')}</span>
                      <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
                    </button>
                    <button
                      on:click={() => {
                        handlePropertiesClick(study);
                        handleMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      disabled={!canConfigureStudies}
                      class:opacity-50={!canConfigureStudies}
                      title={!canConfigureStudies ? tui($tr, $le, 'shared.insufficientPermissions', 'Insufficient permissions') : ''}
                    >
                      <span>{tui($tr, $le, 'studies.configure', 'Configure study')}</span>
                      <FontAwesomeIcon icon={faCog} class="text-sky-800 dark:text-white/70" />
                    </button>
                    <button
                      on:click={() => {
                        handleAuditLogClick(study);
                        handleMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      disabled={!canViewAuditLogs}
                      class:opacity-50={!canViewAuditLogs}
                    >
                      <span>{tui($tr, $le, 'studies.auditLogs', 'View audit logs')}</span>
                      <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
                    </button>
                    <button
                      on:click={() => {
                        handleDeleteClick(study);
                        handleMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      disabled={!canDeleteStudies}
                      class:opacity-50={!canDeleteStudies}
                      title={!canDeleteStudies ? tui($tr, $le, 'shared.insufficientPermissions', 'Insufficient permissions') : ''}
                    >
                      <span>{tui($tr, $le, 'studies.delete', 'Delete study')}</span>
                      <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                    </button>
                  </div>
                {/if}
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <div class="text-center py-8 text-gray-500 dark:text-gray-400">
      {tui($tr, $le, 'studies.noStudies', 'No studies found')}
    </div>
  {/if}
</div>

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>

<style>
.menu-dropdown {
  transition: none;
}
</style>
